import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useCreateChannel } from "@/api/mutations/channels";
import type { ChannelNatsCredentials } from "@/types";
import {
  validateCreateChannel,
  parseConfig,
  KNOWN_CHANNEL_TYPES,
  type CreateChannelInput,
  type ValidationError,
} from "@/api/schemas/channel";
interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** #185/#186: Master returnt channelToken + NATS-Bundle nur einmal — Parent
   *  zeigt den ProvisioningInstructionsDialog (sonst verschwindet das Bundle
   *  mit Dialog-Unmount). */
  onCreated: (result: {
    channelId: string;
    channelName: string;
    channelType: string;
    channelToken: string;
    natsCredentials: ChannelNatsCredentials | null;
  }) => void;
}

const EMPTY: CreateChannelInput = { type: "whatsapp", name: "", configJson: "{}" };

export function CreateChannelDialog({ open, onOpenChange, onCreated }: Props) {
  const [form, setForm] = useState<CreateChannelInput>(EMPTY);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);

  const mutation = useCreateChannel();

  function fieldError(field: ValidationError["field"]) {
    return errors.find((e) => e.field === field)?.message;
  }

  function handleClose(o: boolean) {
    if (!o) {
      setForm(EMPTY);
      setErrors([]);
      setServerError(null);
    }
    onOpenChange(o);
  }

  function handleSubmit() {
    const v = validateCreateChannel(form);
    setErrors(v);
    setServerError(null);
    if (v.length > 0) return;

    const parsed = parseConfig(form.configJson);
    if (!parsed.ok) return; // bereits in errors

    mutation.mutate(
      { type: form.type, name: form.name, config: parsed.value },
      {
        onSuccess: ({ channel, channelToken, natsCredentials }) => {
          toast.success(`Channel ${channel.name} erstellt`);
          onCreated({
            channelId: channel.id,
            channelName: channel.name,
            channelType: channel.type,
            channelToken,
            natsCredentials,
          });
          onOpenChange(false);
        },
        onError: (err) => {
          const msg = (err as Error).message ?? "Unbekannter Fehler";
          setServerError(msg);
          toast.error(`Create fehlgeschlagen: ${msg}`);
        },
      },
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent data-testid="create-channel-dialog">
          <DialogHeader>
            <DialogTitle>Neuer Channel</DialogTitle>
            <DialogDescription>
              Master gibt einmalig ein NATS-Bundle + Start-Anweisung für den Plugin-Container zurück.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="channel-type">Type</Label>
              <Input
                id="channel-type"
                list="channel-type-suggestions"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                placeholder="z.B. slack"
                data-testid="channel-field-type"
              />
              <datalist id="channel-type-suggestions">
                {KNOWN_CHANNEL_TYPES.map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
              {fieldError("type") && (
                <div className="text-xs text-red" data-testid="channel-error-type">
                  {fieldError("type")}
                </div>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="channel-name">Name *</Label>
              <Input
                id="channel-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Hauptchannel WhatsApp"
                data-testid="channel-field-name"
              />
              {fieldError("name") && (
                <div className="text-xs text-red" data-testid="channel-error-name">
                  {fieldError("name")}
                </div>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="channel-config">Config (JSON)</Label>
              <Textarea
                id="channel-config"
                value={form.configJson}
                onChange={(e) => setForm({ ...form, configJson: e.target.value })}
                rows={6}
                className="font-mono text-xs"
                data-testid="channel-field-config"
              />
              {fieldError("configJson") && (
                <div className="text-xs text-red" data-testid="channel-error-config">
                  {fieldError("configJson")}
                </div>
              )}
            </div>
          </div>

          {serverError && (
            <div className="text-sm text-red" data-testid="channel-server-error">
              {serverError}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => handleClose(false)} data-testid="channel-cancel">
              Abbrechen
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={mutation.isPending}
              data-testid="channel-submit"
            >
              {mutation.isPending ? "..." : "Channel erstellen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </>
  );
}
